/** Сессия embed: JWT и organization_id. Режим client (виджет) или trainer (staff). Ключ — slug организации.
 * Дополнительно хранится глобальная сессия (GLOBAL_KEY) — последний активный JWT,
 * чтобы пользователь оставался залогиненным при переходе в каталог (orgSlug = null).
 */

import { clearSuperadminOrgId } from './superadmin-org';

const PREFIX = 'domcrm_embed_session_';
const GLOBAL_KEY = 'domcrm_embed_global_session';

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => l());
}

export function subscribeEmbedSession(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export type EmbedMode = 'client' | 'trainer';

export type EmbedSessionPayload = {
  accessToken: string;
  organizationId: number | null;
  embedMode?: EmbedMode;
  refreshToken?: string;
  /** slug последней активной организации — для навигации из глобального каталога. */
  orgSlug?: string;
};

function parseSessionPayload(raw: string | null): EmbedSessionPayload | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as {
      accessToken?: unknown;
      organizationId?: unknown;
      embedMode?: unknown;
      refreshToken?: unknown;
      orgSlug?: unknown;
    };
    const accessToken = typeof o.accessToken === 'string' ? o.accessToken : '';
    const organizationIdRaw = o.organizationId;
    const organizationId =
      organizationIdRaw == null || organizationIdRaw === ''
        ? null
        : typeof organizationIdRaw === 'number'
          ? organizationIdRaw
          : Number(organizationIdRaw);
    if (!accessToken || (organizationId != null && !Number.isFinite(organizationId))) return null;
    const modeRaw = o.embedMode;
    const embedMode: EmbedMode = modeRaw === 'trainer' ? 'trainer' : 'client';
    const refreshToken = typeof o.refreshToken === 'string' ? o.refreshToken : undefined;
    const orgSlug = typeof o.orgSlug === 'string' ? o.orgSlug : undefined;
    return { accessToken, organizationId, embedMode, refreshToken, orgSlug };
  } catch {
    return null;
  }
}

export function getEmbedSession(orgSlug: string | null): EmbedSessionPayload | null {
  if (!orgSlug || typeof localStorage === 'undefined') return null;
  return parseSessionPayload(localStorage.getItem(PREFIX + orgSlug));
}

/** Читает глобальную сессию (последний залогиненный пользователь). */
export function getGlobalEmbedSession(): EmbedSessionPayload | null {
  if (typeof localStorage === 'undefined') return null;
  return parseSessionPayload(localStorage.getItem(GLOBAL_KEY));
}

export function setGlobalEmbedSession(payload: EmbedSessionPayload): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(GLOBAL_KEY, JSON.stringify(payload));
  notify();
}

export function setEmbedSession(orgSlug: string, payload: EmbedSessionPayload): void {
  const withSlug: EmbedSessionPayload = { ...payload, orgSlug };
  localStorage.setItem(PREFIX + orgSlug, JSON.stringify(withSlug));
  // Всегда обновляем глобальную сессию — «последний вошедший» tenant
  localStorage.setItem(GLOBAL_KEY, JSON.stringify(withSlug));
  notify();
}

export function clearEmbedSession(orgSlug: string | null): void {
  if (!orgSlug || typeof localStorage === 'undefined') return;
  localStorage.removeItem(PREFIX + orgSlug);
  // Если глобальная сессия была от этого же слага — очищаем и её
  const global = getGlobalEmbedSession();
  if (global?.orgSlug === orgSlug) {
    localStorage.removeItem(GLOBAL_KEY);
    clearSuperadminOrgId();
  }
  notify();
}

export function clearGlobalEmbedSession(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(GLOBAL_KEY);
  clearSuperadminOrgId();
  notify();
}
